import { activeRightStack, navigateLeftStack } from '@/app/store';
import { openInvite } from '@/features/invite';
import { formatUserIdToJid } from '@/shared/utils';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useUsersList } from './useUsersList';

/**
 * ViewModel for UsersList (main list) and UserItem (single row).
 * Owns: navigation, filtering, derived render data.
 * No dispatch/selector logic allowed in the View.
 */
export const useUsersListViewModel = (config = {}) => {
   const dispatch = useDispatch();
   const { data: users, loading, loadMore, hasMore, searchText, handleSearch } = useUsersList();

   const isSelectable = config.selectable ?? false;
   const currentSelected = useMemo(() => config.selectedUsers || [], [config.selectedUsers]);
   const handleSelectionToggle = config.onSelectionChange;
   const excludedUsers = useMemo(() => config.excludedUsers || [], [config.excludedUsers]);

   const filteredUsers = useMemo(() => {
      if (!excludedUsers || excludedUsers.length === 0) return users;
      return users.filter(user => !excludedUsers.includes(user.userId));
   }, [users, excludedUsers]);

   const handleSearchChange = useCallback(
      val => {
         handleSearch(val);
      },
      [handleSearch],
   );

   const handleOpenUser = useCallback(
      userId => {
         if (config.onUserClick) {
            config.onUserClick(userId);
            return;
         }
         dispatch(
            activeRightStack({
               activeStack: 'chats',
               activeChatId: userId,
               activeChatJid: formatUserIdToJid(userId),
            }),
         );
      },
      [dispatch, config],
   );

   const handleNavigateNewGroup = useCallback(() => {
      dispatch(navigateLeftStack({ activeStack: 'chats', subStack: 'newgroup' }));
   }, [dispatch]);

   const handleOpenInviteFlow = useCallback(() => {
      openInvite();
   }, []);

   return {
      filteredUsers,
      loading,
      loadMore,
      hasMore,
      searchText,
      isSelectable,
      currentSelected,
      handleSelectionToggle,
      handleSearchChange,
      handleOpenUser,
      handleNavigateNewGroup,
      handleOpenInviteFlow,
   };
};
